import { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import OceanGround from './OceanGround.jsx'
import SceneManager from './SceneManager.jsx'
import * as THREE from 'three/webgpu'
import { Environment } from '@react-three/drei'
import { useGainMapEnvironment } from './useGainMapEnvironment.js'
import { Instances, Saxhq } from './models/saxhq.jsx'

// Detect mobile at module load time — used to select HDRI resolution.
// A 4GB iOS device crashes with the full 3840×1920 HDRI; the 75% variant
// (2880×1440) reduces GPU cost from ~56 MB to ~32 MB.
const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth <= 768

const HDRI_FILES = IS_MOBILE
  ? ['/textures/hdri/sky-mobile.webp', '/textures/hdri/sky-gainmap-mobile.webp', '/textures/hdri/sky.json']
  : ['/textures/hdri/sky.webp', '/textures/hdri/sky-gainmap.webp', '/textures/hdri/sky.json']

// Color grading values — module-level constant, never changes at runtime
const COLOR_GRADING = {
  exposure: 1.0,
  contrast: 1.0,
  saturation: 1.33,
  brightness: 0.0,
  highlights: 0.66,
  shadows: 0.66,
  warmth: 0.033,
  tint: 0.00,
}

/**
 * ScreenPlane - Displays a single texture from the scene manager
 * 
 * Simple implementation that just shows the rendered scene texture.
 * In the future, this can be extended to support:
 * - Multiple texture blending for scene transitions
 * - Color grading and post-processing effects
 * - Shader-based transitions between scenes
 */
function ScreenPlane({ texture, selectiveBloomEnabled = false }) {
  const { camera, viewport } = useThree()
  const meshRef = useRef()
  
  // Create shader material with useMemo
  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      uniforms: {
        texture1: { value: texture },
        // Color grading controls — initialised from COLOR_GRADING constant
        exposure:   { value: COLOR_GRADING.exposure },
        contrast:   { value: COLOR_GRADING.contrast },
        saturation: { value: COLOR_GRADING.saturation },
        brightness: { value: COLOR_GRADING.brightness },
        highlights: { value: COLOR_GRADING.highlights },
        shadows:    { value: COLOR_GRADING.shadows },
        warmth:     { value: COLOR_GRADING.warmth },
        tint:       { value: COLOR_GRADING.tint },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D texture1;
        uniform float exposure;
        uniform float contrast;
        uniform float saturation;
        uniform float brightness;
        uniform float highlights;
        uniform float shadows;
        uniform float warmth;
        uniform float tint;
        varying vec2 vUv;
        
        // Convert RGB to luminance
        float getLuminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        
        // Adjust saturation
        vec3 adjustSaturation(vec3 color, float sat) {
          float lum = getLuminance(color);
          return mix(vec3(lum), color, sat);
        }
        
        // Adjust contrast
        vec3 adjustContrast(vec3 color, float cont) {
          return (color - 0.5) * cont + 0.5;
        }
        
        // Apply color temperature (warmth) and tint
        vec3 applyWhiteBalance(vec3 color, float temp, float tintValue) {
          // Warmth: negative = cooler (blue), positive = warmer (orange)
          // Tint: negative = green, positive = magenta
          
          // Temperature adjustment (blue-orange axis)
          vec3 warmColor = color;
          if (temp > 0.0) {
            // Warmer: boost red/yellow, reduce blue
            warmColor.r += temp * 0.3;
            warmColor.g += temp * 0.15;
            warmColor.b -= temp * 0.2;
          } else {
            // Cooler: reduce red, boost blue
            warmColor.r += temp * 0.2;
            warmColor.b -= temp * 0.3;
          }
          
          // Tint adjustment (green-magenta axis)
          if (tintValue > 0.0) {
            // Magenta: boost red and blue
            warmColor.r += tintValue * 0.2;
            warmColor.b += tintValue * 0.2;
            warmColor.g -= tintValue * 0.1;
          } else {
            // Green: boost green
            warmColor.g -= tintValue * 0.3;
          }
          
          return warmColor;
        }
        
        // Adjust highlights and shadows
        vec3 adjustHighlightsShadows(vec3 color, float highlightMult, float shadowMult) {
          float lum = getLuminance(color);
          
          // Smooth transition between shadows and highlights
          float shadowFactor = smoothstep(0.0, 0.5, lum);
          float highlightFactor = smoothstep(0.5, 1.0, lum);
          
          vec3 result = color;
          result = mix(result * shadowMult, result, shadowFactor);
          result = mix(result, result * highlightMult, highlightFactor);
          
          return result;
        }
        
        void main() {
          vec4 color1 = texture2D(texture1, vUv);
          
          // Apply color grading
          vec4 gradedColor = color1;
          
          // Apply exposure
          gradedColor.rgb *= exposure;
          
          // Apply white balance (warmth and tint)
          gradedColor.rgb = applyWhiteBalance(gradedColor.rgb, warmth, tint);
          
          // Apply highlights and shadows
          gradedColor.rgb = adjustHighlightsShadows(gradedColor.rgb, highlights, shadows);
          
          // Apply brightness
          gradedColor.rgb += brightness;
          
          // Apply saturation
          gradedColor.rgb = adjustSaturation(gradedColor.rgb, saturation);
          
          // Apply contrast
          gradedColor.rgb = adjustContrast(gradedColor.rgb, contrast);
          
          gl_FragColor = gradedColor;
        }
      `
    })
  }, [texture])
  
  // Handle selective bloom layer assignment
  useEffect(() => {
    if (meshRef.current) {
      if (selectiveBloomEnabled) {
        meshRef.current.layers.enable(1) // Enable layer 1 for selective bloom
      } else {
        meshRef.current.layers.disable(1) // Disable layer 1
      }
    }
  }, [selectiveBloomEnabled])
  
  useFrame(() => {
    if (meshRef.current) {
      // Position plane in front of camera
      meshRef.current.position.copy(camera.position)
      meshRef.current.quaternion.copy(camera.quaternion)
      meshRef.current.translateZ(-5)
    }
  })
  
  return (
    <mesh ref={meshRef} material={shaderMaterial}>
      <planeGeometry args={[viewport.width, viewport.height]} />
    </mesh>
  )
}

/**
 * SceneContents - Main scene component
 * 
 * Renders a single scene (Saxavord spaceport) using the render-to-texture approach.
 * This architecture is scalable - to add more scenes in the future:
 * 1. Add scene content definitions
 * 2. Add SceneManager support for multiple scenes
 * 3. Extend ScreenPlane for scene transitions
 */
function SceneContents({ 
  showHelpers = false, 
  selectiveBloomEnabled = true, 
  onScenesReady, 
  isAtPOI = false, 
  activePOI = null, 
  cameraZoom = 2000, 
  cameraRotation = 0 
}) {
  const dirLight1Ref = useRef()
  const cameraHelper1Ref = useRef()

  // On WebGPU, decode the gainmap triplet ourselves (drei's loader is WebGL-only — see the hook).
  // Returns null on a WebGL renderer, where drei's own <Environment files> path is used instead.
  const gainMapTexture = useGainMapEnvironment(HDRI_FILES)

  // Must be read synchronously: on WebGPU the <Environment> below can NEVER be given files={},
  // not even for the frames before gainMapTexture resolves. drei would immediately run its own
  // WebGL-only GainMapLoader and throw readRenderTargetPixels, killing the whole Canvas.
  const isWebGPU = useThree(state => !!state.gl?.isWebGPURenderer)

  const [scene1Texture, setScene1Texture] = useState(null)
  
  // Control when BakeShadows renders (delay by 1 second after mount)
  const [shouldBakeShadows, setShouldBakeShadows] = useState(false)
  
  // Delay BakeShadows by 1 second - only runs once on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldBakeShadows(true)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [])
  
  // ============================================================================
  // SIMPLE DIRECTIONAL LIGHT CONTROLS
  // ============================================================================
  // Change these values to adjust the light:
  const LIGHT_POSITION = [5, 10, -25]  // [x, y, z] - where the light is
  const LIGHT_TARGET = [1, 1, -10]       // [x, y, z] - where the light points
  // ============================================================================
  
  // Set light target once on mount
  useEffect(() => {
    if (dirLight1Ref.current) {
      dirLight1Ref.current.target.position.set(LIGHT_TARGET[0], LIGHT_TARGET[1], LIGHT_TARGET[2])
      dirLight1Ref.current.target.updateMatrixWorld()
    }
  }, [])

  // Scene 1 Content: Saxavord Scene - Default scene with Saxavord spaceport model
  const scene1Content = useMemo(() => {
    return (
      <>
        {/* Sky environment background.
            WebGPU: map={} takes our own decoded gainmap texture, bypassing drei's extension-based
            loader selection — and renders nothing until it resolves, because passing files={} on
            WebGPU would run drei's WebGL-only loader and take down the Canvas.
            WebGL: files={} uses drei's own gainmap loader exactly as it does on main. */}
        {(!isWebGPU || gainMapTexture) && (
        <Environment
          {...(isWebGPU ? { map: gainMapTexture } : { files: HDRI_FILES })}
          background
          environmentRotation={[0, Math.PI / 1.7, 0]} // Rotate environment to align with scene
          backgroundRotation={[0, Math.PI / 1.7, 0]}
          environmentIntensity={1.33}
          backgroundIntensity={1.66}
          // ground={{
          //   height: 5, // Height of the camera that was used to create the env map (Default: 15)
          //   radius: 120, // Radius of the world. (Default 60)
          //   scale: 80, // Scale of the backside projected sphere that holds the env texture (Default: 1000)
          // }}
        />
        )}
        
        {/* Main directional light simulating sun */}
        <directionalLight 
          ref={dirLight1Ref}
          position={LIGHT_POSITION}
          intensity={4}
          color="#ffffff"
          castShadow 
          shadow-mapSize-width={IS_MOBILE ? 1024 : 2048}
          shadow-mapSize-height={IS_MOBILE ? 1024 : 2048}
          shadow-camera-far={100}
          shadow-camera-left={-3.8}
          shadow-camera-right={3.5}
          shadow-camera-top={3}
          shadow-camera-bottom={-2.5}
          shadow-bias={-0.00025}
        />
        
        {/* Ocean ground plane with fading edges */}
        <OceanGround 
          normalMapScale={0.1} 
          roughnessMapScale={0.01} 
          position={[0, -0.55, 0]}
        />
              
        {/* Saxavord spaceport model */}

        <Instances>
          <Saxhq
            position={[0, 0, 0]} 
            rotation={[0, 0, 0]}
            scale={1}
            castShadow 
            receiveShadow 
          />
        </Instances>
      </>
    )
    // gainMapTexture arrives asynchronously on the WebGPU path — without it in the deps the
    // memo would keep serving the pre-decode render and the sky would never appear.
  }, [gainMapTexture, isWebGPU])

  // ============================================================================
  // CAMERA CONTROL SYSTEM
  // ============================================================================
  // Camera animations are driven entirely by SceneManager's scene-camera-move
  // listener, which mounts CameraMovementController inside the R3F render loop.
  // ============================================================================

  // Callback to capture camera and controls references when scenes are ready
  const handleScenesReady = useCallback((scenesData) => {
    // Call the original onScenesReady prop if provided
    if (onScenesReady) {
      onScenesReady(scenesData)
    }
  }, [onScenesReady])

  return (
    <>
      {/* Scene Manager - renders scene to texture */}
      <SceneManager 
        scene1Content={scene1Content}
        onScene1Texture={setScene1Texture}
        mountScene1={true}
        renderScene1={true}
        onScenesReady={handleScenesReady}
        isAtPOI={isAtPOI}
        activePOI={activePOI}
        cameraZoom={cameraZoom}
        shouldBakeShadows={shouldBakeShadows}
        cameraRotation={cameraRotation}
      />
      
      {/* Full screen plane with texture - follows camera */}
      {scene1Texture && (
        <ScreenPlane 
          texture={scene1Texture}
          selectiveBloomEnabled={selectiveBloomEnabled}
        />
      )}
    </>
  )
}

export default SceneContents
